import en from '../translations/en/default';
import stacktraceEn from '../translations/en/stacktrace';
import zodEn from '../translations/en/zod';

/**
 * 名前空間の定義
 *
 * - DEFAULT: 共通機能・component / 大半の pages.* を担う既定の namespace
 * - ZOD: zod バリデーションエラーメッセージ専用
 * - STACKTRACE: stacktrace 系機能 (issues / products / test-suites / ...) 専用。
 *   defaultNS の ParseKeys 肥大化による tsgo TS2859 を防ぐために分離。
 */
export const NAMESPACES = {
  DEFAULT: 'translation',
  ZOD: 'zod',
  STACKTRACE: 'stacktrace',
} as const;

type NamespaceKey = keyof typeof NAMESPACES;
export type Namespace = (typeof NAMESPACES)[NamespaceKey];

// 後方互換性のために残す
export const defaultNS = NAMESPACES.DEFAULT;
export const zodNS = NAMESPACES.ZOD;
export const stacktraceNS = NAMESPACES.STACKTRACE;

/**
 * 初期読み込み翻訳リソースの定義
 */
export const INITIAL_TRANSLATION_RESOURCES = {
  'en-US': {
    [NAMESPACES.DEFAULT]: en,
    [NAMESPACES.ZOD]: zodEn,
    [NAMESPACES.STACKTRACE]: stacktraceEn,
  },
};

/**
 * ネストされたオブジェクトのキーを再帰的に取得
 */
type RecursiveKeyOf<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ? `${K}.${RecursiveKeyOf<T[K]>}`
          : K
        : never;
    }[keyof T]
  : never;

/**
 * 特定の名前空間の翻訳キーの取得
 */
export type TranslationKey<NS extends Namespace> =
  NS extends typeof NAMESPACES.DEFAULT
    ? RecursiveKeyOf<typeof en>
    : NS extends typeof NAMESPACES.ZOD
      ? RecursiveKeyOf<typeof zodEn>
      : NS extends typeof NAMESPACES.STACKTRACE
        ? RecursiveKeyOf<typeof stacktraceEn>
        : never;

type CustomZodKey = TranslationKey<typeof NAMESPACES.ZOD>;

/**
 * zod国際化対応のエラーパラメータを生成する
 */
export const createI18nZodParams = (
  key: CustomZodKey,
  values?: Record<string, unknown>,
) => ({
  params: {
    i18n: {
      key,
      values,
    },
  },
});
